import React, { useState } from 'react';
import Flatpickr from 'react-flatpickr';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';

import Modal from './Modal';
import { OrderListOrderClazz } from '../../types/orderTypes';
import TextArea from '../inputs/TextArea';
import SimpleCheckBox from '../inputs/Checkbox/SimpleCheckbox';
import { rescheduleOrdersToNewDate } from '../../pages/orders/OrdersUtil';
import Dropdown from '../../components/dropdown/Dropdown';

// TODO: THIS NEEDS TO BE MOVED OUT TO THE BACKEND.
const DEVIATION_DATA = [
  {
    category: {
      no: 'Forsinkelse',
      en: 'Delay',
    },
    reasons: [
      {
        type: {
          no: 'Generell forsinkelse',
          en: 'General delay',
        },
        text: {
          no: 'Vi rekker dessverre ikke å levere pakken din i kveld.',
          en: 'Unfortunately, we do not have time to deliver your package tonight.',
        },
      },
      {
        type: {
          no: 'Feilsendt',
          en: 'Wrongly sent',
        },
        text: {
          no: 'Pakken din har dessverre blitt sendt feil, og ikke ankommet vårt lager i tide for utlevering i dag.',
          en: 'Your package has unfortunately been sent incorrectly, and did not arrive at our warehouse in time for delivery today.',
        },
      },
      {
        type: {
          no: 'Forsinket fra retailer',
          en: 'Delayed from retailer',
        },
        text: {
          no: 'Pakken din er dessverre forsinket inn til oss, og som følge av dette har vi måttet utsette leveringsdato.',
          en: 'Your package is unfortunately delayed so we do not have time to deliver it tonight.',
        },
      },
      {
        type: {
          no: 'Sorteringsfeil',
          en: 'Sorting error',
        },
        text: {
          no: 'Grunnet uforutsette problemer klarer vi dessverre ikke å levere til deg i kveld.',
          en: 'Due to unforeseen problems, we are unfortunately unable to deliver to you tonight.',
        },
      },
    ],
  },
  {
    category: {
      no: 'Kapasitetsproblemer',
      en: 'Capacity problems',
    },
    reasons: [
      {
        type: {
          no: 'Backup rute',
          en: 'Backup route',
        },
        text: {
          no: 'Grunnet uforutsette problemer har vi måttet utsette leveransen din.',
          en: 'Due to unforeseen problems, we have had to postpone your delivery.',
        },
      },
      {
        type: {
          no: 'Pakke ikke plass i bilen',
          en: 'No space for package in the car',
        },
        text: {
          no: 'Grunnet uforutsette problemer klarer vi dessverre ikke å levere til deg i kveld.',
          en: 'Due to unforeseen problems, we are unfortunately unable to deliver to you tonight.',
        },
      },
    ],
  },
  {
    category: {
      no: 'Last Mile',
      en: 'Last Mile',
    },
    reasons: [
      {
        type: {
          no: 'Avvikslevering',
          en: 'Deviation delivery',
        },
        text: {
          no: 'Vi fikk dessverre ikke levert til deg i kveld, og vi håper ny leveringsdato passer.',
          en: 'Unfortunately, we were unable to deliver to you this evening, and we hope the new delivery date will be suitable.',
        },
      },
      {
        type: {
          no: 'Trafikale forsinkelser',
          en: 'traffic delays',
        },
        text: {
          no: 'Din leveranse har dessverre blitt forsinket på grunn av utfordrende trafikkforhold.',
          en: 'Your delivery has unfortunately been delayed due to challenging traffic conditions.',
        },
      },
    ],
  },
  {
    category: {
      no: 'Custom',
      en: 'Custom',
    },
    reasons: [
      {
        type: {
          no: 'Annet',
          en: 'Other',
        },
        text: {
          no: '',
          en: '',
        },
      },
    ],
  },
];

interface Deviation {
  name: string;
  value: string;
  text: string | null;
  disabled: boolean;
}

interface SelectDeliveryDateMultipleModalProps {
  orders: OrderListOrderClazz[];
  onCloseModal: () => void;
  triggerReload: boolean;
}

const flattenDeviationData = (lang: string): Deviation[] => {
  const flatList = [];

  for (let i = 0; i < DEVIATION_DATA.length; i++) {
    flatList.push({
      name: DEVIATION_DATA[i].category[lang],
      value: DEVIATION_DATA[i].category[lang],
      text: null,
      disabled: true,
    });
    for (let j = 0; j < DEVIATION_DATA[i].reasons.length; j++) {
      flatList.push({
        name: DEVIATION_DATA[i].reasons[j].type[lang],
        value: DEVIATION_DATA[i].reasons[j].type[lang],
        text: DEVIATION_DATA[i].reasons[j].text[lang],
        disabled: false,
      });
    }
  }
  return flatList;
};

const SelectDeliveryDateMultipleModal = ({
  orders,
  onCloseModal,
  triggerReload,
}: SelectDeliveryDateMultipleModalProps): JSX.Element => {
  const dropdownOptions = flattenDeviationData('no');
  const dropDownNames = dropdownOptions.map(opt =>
    opt.disabled ? (
      <div
        style={{
          fontFamily: 'Helvetica',
          fontSize: '14px',
          color: '#71717A',
          pointerEvents: 'none',
        }}
      >
        {opt.name.toUpperCase()}
      </div>
    ) : (
      <div
        style={{ fontFamily: 'Helvetica', fontSize: '14px', color: '#1F2937' }}
      >
        {opt.name}
      </div>
    )
  );

  const [newDate, setNewDate] = useState<Date[]>(
    new Array<Date>(dayjs().add(1, 'day').toDate())
  );
  const [message, setMessage] = useState<string>(dropdownOptions[1].text);
  const [sendSms, setSendSms] = useState(true);
  const [sendEmail, setSendEmail] = useState(false);
  const [closeDeviation, setCloseDeviation] = useState(true);
  const [closeCourierDeviations, setCloseCourierDeviations] = useState(true);
  const [removeFromBundle, setRemoveFromBundle] = useState(false);
  const [acceptAnyDate, setAcceptAnyDate] = useState(false);

  const DATE_FORMAT = 'YYYY-MM-DD';
  const inputStyle = {
    fontSize: '1.5rem',
    paddingTop: '8px',
    paddingBottom: '8px',
    width: '50%',
    minWidth: '200px',
  };

  const onDropdownSelect = (name: any): void => {
    const target = dropdownOptions.find(
      opt => opt.name.toLowerCase() === name.props.children.toLowerCase()
    );

    if (!target || target.disabled) return;

    setMessage(target.text);
  };

  const createCsvFileName = (orderIds, newDate) => {
    const currentFormatted = dayjs().format(DATE_FORMAT);
    const newFormatted = dayjs(newDate).format(DATE_FORMAT);
    const len = orderIds.length;
    return `${currentFormatted}_new_date_${newFormatted}_for_${len}_orders.csv`;
  };

  const createCsv = (
    oldOrders: Array<OrderListOrderClazz>,
    orderIds: Array<string>,
    newDate: Date
  ) => {
    const header =
      'Id,Order Reference,Sender,Sender Email,Date created,Original delivery date,New delivery date\n';
    const content = oldOrders
      .filter(order => orderIds.includes(order.id))
      .map(
        order =>
          `${order.id},${order.orderReference},${order.pickupName},${
            order.pickupEmail
          },${order.createdDate},${order.deliverFrom},${dayjs(newDate).format(
            DATE_FORMAT
          )}`
      )
      .join('\n');

    return header + content;
  };

  const rescheduleOrders = async () => {
    const result = await rescheduleOrdersToNewDate(
      orders,
      newDate[0],
      sendSms,
      sendEmail,
      closeDeviation,
      closeCourierDeviations,
      removeFromBundle,
      acceptAnyDate,
      message
    );

    const errors = result.errors || [];
    const orderIds = result.changedOrderIds || [];
    const removedFromBundlesIds = result.removedFromBundleIds || [];
    const differentDateBundles = result.differentDateBundleIds || [];

    if (errors.length === 0 && orderIds.length > 0) {
      const unchangedOrders = orders.length - orderIds.length;
      const footer =
        sendSms || sendEmail
          ? 'Only customers with a changed delivery date got notifications'
          : '';
      let mainText = '';
      if (removedFromBundlesIds.length > 0) {
        mainText += '<h6>Bundles with removed order(s):</h6><ul>';
        removedFromBundlesIds.forEach(id => {
          mainText = `${mainText}<li style="text-align: left">Bundle ${id}<li>`;
        });
        mainText += '</ul>';
      } else if (removeFromBundle) {
        mainText += 'No order was contained in any bundle';
      }
      if (differentDateBundles.length > 0) {
        mainText +=
          '<h6 style="color: red">Bundles containing different delivery dates:</h6><ul>';
        differentDateBundles.forEach(id => {
          mainText = `${mainText}<li style="text-align: left">Bundle ${id}<li>`;
        });
        mainText += '</ul>';
      }
      mainText += `
        <h6>Summary</h6>
        <ul>
            <li style="text-align: left">${orders.length} order(s) handled in total</li>
            <li style="text-align: left">${orderIds.length} order(s) changed to the new delivery date</li>
            <li style="text-align: left">${unchangedOrders} order(s) had already the new delivery date</li>
        </ul>`;
      await Swal.fire({
        title: 'Success!',
        html: mainText,
        icon: 'success',
        footer: `<div style="text-align: left; font-size: 1.5rem; margin-bottom: 5px;">${footer}</div>`,
        confirmButtonText: 'List of changed orders as csv file',
      }).then(() => {
        const csvFileName = createCsvFileName(orderIds, newDate[0]);
        saveAs(
          new Blob([createCsv(orders, orderIds, newDate[0])], {
            type: 'text/csv',
          }),
          csvFileName
        );
        Swal.fire({
          title: 'Success!',
          icon: 'success',
          html: `<h6>${csvFileName}</h6> 
            <div>File downloaded.</div>
            <div>Please check your download folder!</di>`,
        }).then(() => {
          if (triggerReload) {
            window.location.reload();
          } else {
            onCloseModal();
          }
        });
      });
    } else if (errors.length === 0 && orderIds.length === 0) {
      await Swal.fire({
        title: 'There was nothing to do!',
        icon: 'info',
        text: 'All selected orders had already the new delivery date. Maybe you selected a wrong date?',
      });
    } else {
      await Swal.fire({
        title: 'Failure!',
        icon: 'error',
        html: `<div>Maybe you selected a wrong date?</div>
        <div>None of the ${orders.length} handled order(s) were changed!</div>
        <div style="margin-bottom: 5px;">Showing only the first error of total ${errors.length} error(s)</div>
        <div style="text-align: left; font-size: 1.5rem; margin-bottom: 5px;">${errors[0].message}</div>`,
        footer:
          '<div style="text-align: left; font-size: 1.5rem; margin-bottom: 5px;">No customer got any notifications</div>',
      });
    }
  };

  const borderedBoxStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '24px 0px 14px 0px',
    fontFamily: 'Helvetica',
    fontSize: '16px',
    border: '1px solid #cccccc',
    borderRadius: '5px',
    padding: '10px',
  };
  const orderText = orders.length > 1 ? 'order' : 'order (s)';
  const modalTitle = `Select new delivery date for ${orders.length} ${orderText}`;
  const changeButton = {
    title: 'Change',
    type: 'success',
    onClick: () => {
      rescheduleOrders();
    },
    loading: false,
    disabled: false,
  };

  return (
    <Modal
      title={modalTitle}
      onClose={() => onCloseModal()}
      buttons={[changeButton]}
      width={640}
    >
      <div style={{ margin: '14px' }}>
        <div style={{ fontFamily: 'Helvetica', fontSize: '14px' }}>
          Select new date, reason for deviation and inform customer(s) by SMS.
        </div>
        <div style={{ ...borderedBoxStyle, justifyContent: 'flex-start' }}>
          <div style={{ marginRight: '25px' }}>
            <SimpleCheckBox
              onChange={() => setSendSms(!sendSms)}
              selected={sendSms}
              label="Send SMS"
              labelId="sendSmsCheckbox"
            />
          </div>
          <div>
            <SimpleCheckBox
              onChange={() => setSendEmail(!sendEmail)}
              selected={sendEmail}
              label="Send email"
              labelId="sendEmailCheckbox"
            />
          </div>
        </div>
        <div style={borderedBoxStyle}>
          <div>
            <SimpleCheckBox
              onChange={() => setRemoveFromBundle(!removeFromBundle)}
              selected={removeFromBundle}
              label="Remove from bundle"
              labelId="removeFromBundleCheckbox"
            />
          </div>
          <div>
            <SimpleCheckBox
              onChange={() => setAcceptAnyDate(!acceptAnyDate)}
              selected={acceptAnyDate}
              label="Accept any date"
              labelId="removeFromBundleCheckbox"
            />
          </div>
          <div>
            <SimpleCheckBox
              onChange={() => setCloseDeviation(!closeDeviation)}
              selected={closeDeviation}
              label="Close open deviations"
              labelId="closeDeviationCheckbox"
            />
          </div>
          <div>
            <SimpleCheckBox
              onChange={() =>
                setCloseCourierDeviations(!closeCourierDeviations)
              }
              selected={closeCourierDeviations}
              label="Close open courier deviations"
              labelId="closeCourierDeviationsCheckbox"
            />
          </div>
        </div>
        <div style={{ marginBottom: '6px' }}>
          <h6>New delivery date</h6>
        </div>
        <div style={{ marginBottom: '15px' }}>
          <Flatpickr
            onClose={selectedDates => setNewDate(selectedDates)}
            options={{
              dateFormat: 'l d M',
              defaultDate: newDate[0],
              shorthandCurrentMonth: true,
              locale: { firstDayOfWeek: 1 },
            }}
            render={(_, ref) => (
              <input
                style={inputStyle}
                className={'form-control'}
                type="text"
                ref={ref}
              />
            )}
          />
        </div>
        <div style={{ marginBottom: '6px' }}>
          <h6>Reason for change</h6>
        </div>
        <div style={{ marginBottom: '20px', width: '50%' }}>
          <Dropdown
            names={dropDownNames}
            placeholder="FORSINKELSE"
            onChange={onDropdownSelect}
          />
        </div>
        <div
          style={{
            backgroundColor: '#F9F9F9',
            padding: '24px',
            borderRadius: '24px',
            fontFamily: 'Helvetica',
            fontWeight: 400,
            fontSize: '16px',
          }}
        >
          <p>Kjære kunde.</p>
          <p>
            Leveringsvinduet for varene fra SENDER med Porterbuddy/Instabox
            (PB/Pakke-ID ORDERID) har blitt flyttet frem.
          </p>
          <p>Nytt leveringsvindu: NEW TIME.</p>
          <div style={{ margin: '14px 0px' }}>
            <TextArea
              onChange={event => setMessage(event.target.value)}
              value={message}
            />
          </div>
          <p>Du kan selv endre leveringsvindu på sporingssiden: TRACKING URL</p>
          <p>Hilsen Porterbuddy/Instabox</p>
        </div>
      </div>
    </Modal>
  );
};

export default SelectDeliveryDateMultipleModal;
